// Detect stale-chunk errors after a deploy and auto-reload once.
//
// When we ship a new frontend, the chunk filenames change (e.g.
// 7014.cc247272.chunk.js -> 7014.ab012345.chunk.js). Browsers that
// were already on the site have the old main.js cached and will try
// to load the old chunk paths, which now 404. React Router lazy-load
// calls then throw ChunkLoadError and the UI dies.
//
// This handler catches that, sets a one-shot guard in sessionStorage
// so we don't infinite-loop, and reloads with cache busted.

const RELOAD_GUARD_KEY = '__hutson_chunk_reload_at__';
const GUARD_WINDOW_MS = 30 * 1000; // 30s — don't reload more than once per window

const isChunkLoadError = (err) => {
  if (!err) return false;
  // webpack-style ChunkLoadError
  if (err.name === 'ChunkLoadError') return true;
  const msg = String(err.message || err);
  return (
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
};

const reloadOnce = () => {
  try {
    const lastReload = parseInt(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0', 10);
    if (Date.now() - lastReload < GUARD_WINDOW_MS) {
      // We already reloaded recently — don't loop. Something else is wrong.
      // eslint-disable-next-line no-console
      console.warn('[chunkReload] Skipping reload — already reloaded recently.');
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch (_) {
    // sessionStorage unavailable (private mode etc.) — fall through and reload anyway
  }
  // Force a full reload that bypasses HTTP cache.
  window.location.reload();
};

export const installChunkReloadHandler = () => {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event)) {
      reloadOnce();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      reloadOnce();
    }
  });
};
